import { axiosInstance } from '@/axios'
import config from '@/config'
import Vue from 'vue'
import { basePanelsRoute, entryApprovalStatuses, entryStatusesEventMap } from '@/constants'

export default {
  state: {
    collectionSlug: null,
    fields: null,
    collectionEntries: null,
    contentApprovalEntries: null,
    filtersObj: {},
    selectedEntry: null,
    selectedEntryOrigin: null,
    searchWord: null,
    needToRefilterColection: false
  },
  mutations: {
    ADD_CONTENT_APPROVAL_ENTRY (state, newCollectionEntry) {
      state.contentApprovalEntries.push(newCollectionEntry)
    },
    ADD_COLLECTION_ENTRY (state, newCollectionEntry) {
      state.collectionEntries.push(newCollectionEntry)
    },
    UPDATE_COLLECTION_ENTRY (state, { newCollectionEntry, isContentApproval }) {
      state.selectedEntry = newCollectionEntry
      const collection = isContentApproval ? 'contentApprovalEntries' : 'collectionEntries'
      state[collection].forEach((entry, index) => {
        if (entry.data.id === newCollectionEntry.data.id) {
          Vue.set(state[collection], index, newCollectionEntry)
        }
      })
    },
    UPDATE_COLLECTION_ENTRY_STATUS (state, newCollectionEntry) {
      state.selectedEntry = newCollectionEntry
      state.contentApprovalEntries.forEach((entry, index) => {
        if (entry.data.id === newCollectionEntry.data.id) {
          Vue.set(state.contentApprovalEntries, index, newCollectionEntry)
        }
      })
      if (newCollectionEntry.data.published_status === entryApprovalStatuses.PUBLISHED) {
        state.collectionEntries.push(newCollectionEntry)
      }
    },
    SET_COLLECTION_SLUG (state, collectionSlug) {
      state.collectionSlug = collectionSlug
    },
    SET_CONTENT_APPROVAL_ENTRIES (state, collectionEntries) {
      state.contentApprovalEntries = collectionEntries
    },
    SET_COLLECTION_ENTRIES (state, collectionEntries) {
      state.collectionEntries = collectionEntries
    },
    REMOVE_FILTER (state, { filterKey, index }) {
      state.filtersObj[filterKey].splice(index, 1)
      if (state.filtersObj[filterKey].length === 0) {
        delete state.filtersObj[filterKey]
      }
    },
    ADD_FILTER (state, { filterKey, filterValue }) {
      if (!state.filtersObj[filterKey]) {
        Vue.set(state.filtersObj, filterKey, [])
      }
      state.filtersObj[filterKey].push(filterValue)
    },
    SET_SELECTED_ENTRY (state, collectionEntry) {
      state.selectedEntry = collectionEntry
    },
    SET_ENTRY_ORIGIN (state, origin) {
      state.selectedEntryOrigin = origin
    },
    DELETE_SELECTED_ENTRY (state, selectedEntry) {
      state.collectionEntries = state.collectionEntries.filter(obj => obj.data.id !== selectedEntry.data.id)
    },
    SET_FILTERS (state, filtersObj) {
      state.filtersObj = filtersObj
    },
    SET_SEARCH_WORD (state, searchWord) {
      state.searchWord = searchWord
    },
    SET_REFILTER_COLLECTIONS_AFTER_LOADING (state, data) {
      state.needToRefilterColection = data
    },
    SET_FIELDS (state, data) {
      state.fields = data
    }
  },
  actions: {
    getLibraryFields ({ commit, rootState }) {
      const librarySlug = rootState.base.library.slug
      return axiosInstance.get('library/' + librarySlug + '/fields').then((response) => {
        commit('SET_FIELDS', response.data)
      })
    },
    getLibraryRootCollections ({ state, rootState, commit }) {
      const librarySlug = rootState.base.library.slug
      return axiosInstance.get('library/' + librarySlug + '/root-collections').then((response) => {
        const rootCollectionSlug = response.data[0].data.slug
        commit('SET_COLLECTION_SLUG', rootCollectionSlug)
        commit('SET_COLLECTION_ENTRIES', response.data[0].entries)
        if (state.needToRefilterColection) {
          return this.dispatch('getFilterCollectionEntries', true)
        }
      })
    },
    getContentApprovalEntries ({ commit, state }) {
      return axiosInstance.get('collections/' + state.collectionSlug + '/content-approval-collection-entries/')
        .then(response => {
          commit('SET_CONTENT_APPROVAL_ENTRIES', response.data.entries)
        })
    },
    getCollectionEntry (context, entryId) {
      return axiosInstance.get('collection-entries/' + entryId)
    },
    addOrRemoveFilter ({ commit, state, rootState }, { filterKey, filterValue, isChecked }) {
      const index = state.filtersObj[filterKey]?.indexOf(filterValue)
      const isFilterValueChecked = index > -1
      if (!isChecked && isFilterValueChecked) {
        commit('REMOVE_FILTER', { filterKey, index })
      } else if (isChecked && !isFilterValueChecked) {
        commit('ADD_FILTER', { filterKey, filterValue })
      }
      if (Object.keys(state.filtersObj).length || state.searchWord) {
        this.dispatch('getFilterCollectionEntries')
      } else {
        if (rootState.base.panelRoute === basePanelsRoute.contentApproval) {
          this.dispatch('getContentApprovalEntries')
        } else {
          this.dispatch('getLibraryRootCollections')
        }
      }
    },
    setFilltersFromRouteAfterRefresh ({ commit, state }, arrayOfFilters) {
      this.dispatch('resetFilterObj')
      for (const filter of arrayOfFilters) {
        const filterKey = filter[0]
        const filterValue = parseInt(filter[1])
        commit('ADD_FILTER', { filterKey, filterValue })
      }
    },
    setSearchWord ({ commit }, searchWord) {
      commit('SET_SEARCH_WORD', searchWord)
    },
    searchResults (context) {
      return this.dispatch('getFilterCollectionEntries', true)
    },
    getFilterCollectionEntries ({ commit, state, rootState }, resetFilter = false) {
      const isContentApproval = rootState.base.panelRoute === basePanelsRoute.contentApproval
      // If we get a text to search, ignore the filterArray
      if (state.collectionSlug) {
        let dataObj = null
        dataObj = {
          filter: state.filtersObj
        }
        if (state.searchWord) {
          dataObj.search = {
            query: state.searchWord,
            type: 'descendants'
          }
        }
        dataObj['content-approval'] = isContentApproval

        commit('SET_REFILTER_COLLECTIONS_AFTER_LOADING', false)
        return axiosInstance.post('collections/' + state.collectionSlug + '/search/', dataObj)
          .then(response => {
            if (isContentApproval) {
              commit('SET_CONTENT_APPROVAL_ENTRIES', response.data.collection_entries)
            } else {
              commit('SET_COLLECTION_ENTRIES', response.data.collection_entries)
            }
          })
          .catch(() => {
            commit('SET_COLLECTION_ENTRIES', [])
          })
      } else {
        commit('SET_REFILTER_COLLECTIONS_AFTER_LOADING', true)
      }
    },
    resetFilterObj ({ commit }) {
      commit('SET_FILTERS', {})
    },
    selectEntry ({ commit }, collectionEntry) {
      commit('SET_SELECTED_ENTRY', collectionEntry)
    },
    setEntryOrigin ({ commit }, origin) {
      commit('SET_ENTRY_ORIGIN', origin)
    },
    deleteSelectedEntry ({ commit, state }) {
      return axiosInstance.delete('collection-entries/' + state.selectedEntry.data.id).then(() => {
        commit('DELETE_SELECTED_ENTRY', state.selectedEntry)
      })
    },
    updateCollectionEntry ({ commit, state, rootState }, collectionEntry) {
      const isContentApproval = rootState.base.panelRoute === basePanelsRoute.contentApproval
      return axiosInstance.put('collection-entries/' + state.selectedEntry.data.id + '/', collectionEntry)
        .then((res) => {
          commit('UPDATE_COLLECTION_ENTRY', { newCollectionEntry: res.data, isContentApproval })
          return res
        })
        .catch((e) => {
          return e
        })
    },
    postNewCollectionEntry ({ commit, state }, newCollectionEntry) {
      return axiosInstance.post('collections/' + state.collectionSlug + '/add-entry/', newCollectionEntry)
        .then((res) => {
          if (res.data.data.published_status === 'PENDING') {
            commit('ADD_CONTENT_APPROVAL_ENTRY', res.data)
          } else {
            commit('ADD_COLLECTION_ENTRY', res.data)
          }
          return res
        })
        .catch((e) => {
          return e
        })
    },
    addToMyCollection ({ commit }, id) {
      const currentPageUrl = window.location.href
      const data = {
        currentPageUrl: currentPageUrl
      }
      return axiosInstance.post('collection-entries/' + id + '/add-my-units/', data)
    },
    addExternalContentToMyCollection ({ state, commit }, { contentId, entryObj = null }) {
      let entry = entryObj
      if (!entry) {
        entry = state.collectionEntries.find(collection => collection.data.id === contentId)
      }
      const currentPageUrl = window.location.href
      const externalContentData = {
        url: entry.object.url,
        lesson_name: entry.data.title,
        unit_name: entry.data.title,
        lesson_image: entry.data.image,
        class_name: null,
        unit_image: entry.data.image,
        category: null,
        lang: null,
        currentPageUrl: currentPageUrl
      }
      return axiosInstance.post(config.classeUrl + 'api/units/create-external-content-unit/', externalContentData)
    },
    downloadContent ({ commit }, collectionEntryId) {
      window.open(config.baseURL + 'collection-entries/' + collectionEntryId + '/content/')
    },
    previewClasseContent ({ commit }, id) {
      const params = { blockAnswer: true }
      // TODO: Replace this url with backend url
      const url = config.baseURL + 'collection-entries/' + id + '/preview-url/'
      const handle = window.open(url, '_blank', 'width=900,height=750,toolbar=no,location=no,resizable =yes')
      if (!handle || handle.closed || typeof handle.closed === 'undefined') {
        // POPUP BLOCKED
        return false
      }
      handle.window.parameters = JSON.stringify(params)
      handle.moveTo(0, 0)
      handle.resizeTo(screen.width, screen.height)
      console.log('previewClasseContent: ' + id)
    },
    findAndSetEntryById ({ commit, state, rootState }, contentId) {
      let collectionEntries = state.collectionEntries
      if (rootState.base.panelRoute === basePanelsRoute.contentApproval) {
        collectionEntries = state.contentApprovalEntries
      }
      const contentIdsEntry = collectionEntries.find(collection => collection.data.id === contentId)
      commit('SET_SELECTED_ENTRY', contentIdsEntry)
    },
    resetFilterAndSearch ({ commit, state }) {
      this.dispatch('setSearchWord', null)
      this.dispatch('resetFilterObj')
      return this.dispatch('getLibraryRootCollections')
    },
    updateCollectionEntryStatus ({ commit, state }, { id, action, comment = null }) {
      // update published_status and metadata of collection Entry to include new event
      const data = { event: action, comment }
      return axiosInstance.post('collection-entries/' + id + '/content-approval/', data)
        .then((res) => {
          commit('UPDATE_COLLECTION_ENTRY_STATUS', res.data)
          return res
        })
        .catch((e) => {
          return e
        })
    }
  },
  getters: {
    isClassificationSelected: (state) => (filterKey, filterValue) => {
      if (state.filtersObj[filterKey]) {
        return state.filtersObj[filterKey].includes(filterValue)
      }
      return false
    },
    getEntrySubmitted: (state) => {
      return (collectionEntry) => {
        const submitEvent = collectionEntry.data.metadata.events?.find(event => event.event_name === entryStatusesEventMap.PENDING)
        // Get only the data without the time.
        let date = new Date(submitEvent?.timestamp)
        // Format the date
        date = date.toLocaleDateString('en-GB', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit'
        })
        if (submitEvent) {
          return {
            user: submitEvent?.user_fullname,
            date
          }
        } else {
          return false
        }
      }
    },
    getEntryEditors: (state) => (collectionEntry) => {
      let editors = []
      collectionEntry.data.metadata.events.forEach(event => {
        if (event.event_name === entryStatusesEventMap.EDITED) {
          editors.push(event.user_fullname)
        } else if (event.event_name === entryStatusesEventMap.REJECTED) {
          //  reset the editors list after rejection
          editors = []
        }
      })
      return editors.join(', ')
    },
    getLastEntryEventByStatus: (state) => (collectionEntry) => {
      const status = collectionEntry.data.published_status
      let eventName = entryStatusesEventMap[status]
      if (eventName === entryStatusesEventMap.PENDING) {
        eventName = entryStatusesEventMap.EDITED
      }
      const eventsByStatus = collectionEntry.data.metadata.events?.filter(event => event.event_name === eventName)
      if (!(eventsByStatus?.length)) return false
      const lastEditEvent = eventsByStatus[eventsByStatus.length - 1]
      let date = new Date(lastEditEvent?.timestamp)
      // Format the date
      date = date.toLocaleDateString('en-GB', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      })
      if (lastEditEvent) {
        return {
          user: lastEditEvent?.user_fullname,
          date
        }
      } else {
        return false
      }
    },

    getIsDisableSendToApproval: (state) => (collectionEntry) => {
      // If the entry satus is `REJECTED`, send-to-approval is enabled always
      if (collectionEntry.data.published_status === entryApprovalStatuses.REJECTED) {
        return false
      }
      // Check if edit event was occurred after the last submitted event.
      const lastEvent = collectionEntry.data.metadata.events[collectionEntry.data.metadata.events.length - 1]
      return lastEvent.event_name === entryStatusesEventMap.PENDING
    },
    // Returns an array that contains all the entries that match the selected status
    getEntriesByStatus: (state, getters) => (status, searchText) => {
      let statusArr = []
      if (state.contentApprovalEntries) {
        for (const collectionEntry of state.contentApprovalEntries) {
          if (collectionEntry.data.published_status) {
            if (collectionEntry.data.published_status === status) {
              statusArr.push(collectionEntry)
            }
          }
        }
      }
      if (searchText) {
        statusArr = statusArr.filter(entry =>
          entry.data.title?.includes(searchText) || entry.data.metadata?.author?.includes(searchText) || getters.getEntryEditors(entry).includes(searchText)
        )
      }
      return statusArr
    }
  }
}
